/**
 * Curate Bullion slip picks for the league Play lobby rail.
 * Prefer: biggest open pot, biggest won pts, and the gold/bonus line.
 */

import { LEAGUE_VIEWER_USER_ID } from "./constants";
import type { League, LeagueBullionBattleViewerSlip } from "./types";

/**
 * Prefer API `viewerSlip`; fall back to prototype `viewerPicksByUserId` (demo enter path).
 */
export function resolveLeagueBullionViewerSlip(
    league: Pick<League, "bullionBattle">,
    userId?: number | null,
): LeagueBullionBattleViewerSlip | null {
    const battle = league.bullionBattle;
    if (!battle) return null;

    const slip = battle.viewerSlip ?? null;
    const slipPicks = slip?.picks;
    if (slipPicks && typeof slipPicks === "object" && Object.keys(slipPicks).length > 0) {
        return slip;
    }

    if (userId != null && userId > 0) {
        const saved = battle.viewerPicksByUserId?.[String(userId)]
            ?? battle.viewerPicksByUserId?.[String(LEAGUE_VIEWER_USER_ID)];
        const selections = saved?.selections;
        if (selections && typeof selections === "object" && Object.keys(selections).length > 0) {
            return {
                picks: selections,
                bonusPicks: Array.isArray(saved?.bonusPicks) ? saved.bonusPicks : [],
            };
        }
    }

    return slip;
}



/** Fallback before the rail is measured; prefer `leagueBullionLobbyPickLimitForWidth`. */
export const LEAGUE_BULLION_LOBBY_PICK_LIMIT = 6;

export const LEAGUE_BULLION_LOBBY_CARD_WIDTH_PX = 80;
export const LEAGUE_BULLION_LOBBY_CARD_GAP_PX = 8;
/** Horizontal padding on the pick rail (`p-2` × 2). */
export const LEAGUE_BULLION_LOBBY_RAIL_PADDING_PX = 16;

/** How many 80px cards fit in the lobby rail (up to the full slip). */
export function leagueBullionLobbyPickLimitForWidth(
    railWidthPx: number,
    totalPicks: number,
): number {
    if (totalPicks <= 0) return 0;
    const inner = Math.max(0, railWidthPx - LEAGUE_BULLION_LOBBY_RAIL_PADDING_PX);
    const slot = LEAGUE_BULLION_LOBBY_CARD_WIDTH_PX + LEAGUE_BULLION_LOBBY_CARD_GAP_PX;
    const fits =
        slot > 0 ? Math.floor((inner + LEAGUE_BULLION_LOBBY_CARD_GAP_PX) / slot) : 1;
    return Math.min(totalPicks, Math.max(1, fits));
}

export type LeagueBullionLobbyPickOutcome = "open" | "won" | "lost";

export type LeagueBullionLobbyPickCard = {
    key: string;
    market: string;
    selection: string;
    /** Footer value: pot while open, awarded pts when won, 0 when lost. */
    points: number;
    potPoints: number;
    gold: boolean;
    outcome: LeagueBullionLobbyPickOutcome;
    /** Expand detail (My Games column): decimal odds on this line. */
    odds?: number | null;
    /** Regular chip stake on this line. */
    stakeChips?: number;
    /** Settlement result text (e.g. winning side) once settled. */
    settlementResult?: string | null;
};

function sortByDesc(a: number, b: number): number {
    return b - a;
}

/**
 * Pick a small highlight set from the full slip, then order for the lobby:
 * open pot → won pts → gold (deduped).
 */
export function selectLeagueBullionLobbyPickCards(
    cards: LeagueBullionLobbyPickCard[],
    limit: number = LEAGUE_BULLION_LOBBY_PICK_LIMIT,
): LeagueBullionLobbyPickCard[] {
    if (cards.length === 0) return [];
    const max = Math.max(1, Math.floor(limit));
    if (cards.length <= max) return orderLeagueBullionLobbyPickCards(cards);

    const selected = new Map<string, LeagueBullionLobbyPickCard>();
    const take = (card: LeagueBullionLobbyPickCard | undefined) => {
        if (!card || selected.has(card.key) || selected.size >= max) return;
        selected.set(card.key, card);
    };

    const openByPot = cards
        .filter((card) => card.outcome === "open")
        .sort((a, b) => sortByDesc(a.potPoints, b.potPoints));
    const wonByPts = cards
        .filter((card) => card.outcome === "won")
        .sort((a, b) => sortByDesc(a.points, b.points));
    const gold = cards.find((card) => card.gold);
    const restByPot = [...cards].sort((a, b) => sortByDesc(a.potPoints, b.potPoints));

    // Always surface the bonus line, then best correct pts, then best remaining pot.
    take(gold);
    take(wonByPts[0]);
    take(openByPot[0]);

    for (const card of wonByPts) take(card);
    for (const card of openByPot) take(card);
    // Avoid filling with settled misses (0 pts) — bonus is already taken above.
    for (const card of restByPot.filter((row) => row.outcome !== "lost")) take(card);

    return orderLeagueBullionLobbyPickCards([...selected.values()]);
}

/** Story order for the lobby rail: cooking pot → biggest hit → gold bar. */
export function orderLeagueBullionLobbyPickCards(
    cards: LeagueBullionLobbyPickCard[],
): LeagueBullionLobbyPickCard[] {
    if (cards.length <= 1) return cards;

    const ordered: LeagueBullionLobbyPickCard[] = [];
    const used = new Set<string>();
    const push = (card: LeagueBullionLobbyPickCard | undefined) => {
        if (!card || used.has(card.key)) return;
        used.add(card.key);
        ordered.push(card);
    };

    push(
        [...cards]
            .filter((card) => card.outcome === "open")
            .sort((a, b) => sortByDesc(a.potPoints, b.potPoints))[0],
    );
    // Prefer a non-gold win in the middle so the bonus keeps its own end slot.
    const wonSorted = [...cards]
        .filter((card) => card.outcome === "won")
        .sort((a, b) => sortByDesc(a.points, b.points));
    push(wonSorted.find((card) => !card.gold) ?? wonSorted[0]);
    push(cards.find((card) => card.gold));

    for (const card of [...cards].sort((a, b) => sortByDesc(a.potPoints, b.potPoints))) {
        push(card);
    }
    return ordered;
}
